import React, { useState, useEffect } from 'react';
import { adminUsersAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

function AdminUsers() {
  const { hasPermission, user: currentUser } = useAuth();
  
  // Permission checks
  const canCreate = hasPermission('admin_users.create');
  const canEdit = hasPermission('admin_users.update');
  const canDelete = hasPermission('admin_users.delete');

  const [showForm, setShowForm] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(null);
  const [editMode, setEditMode] = useState(null);
  const [selectedAdmin, setSelectedAdmin] = useState(null);
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    full_name: '',
    department: '',
    phone: '',
    role: 'viewer',
  });
  const [passwordData, setPasswordData] = useState({ new_password: '' });
  const [adminUsers, setAdminUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0 });
  const [filters, setFilters] = useState({
    search: '',
    role: '',
    is_active: '',
  });

  useEffect(() => {
    fetchAdminUsers();
  }, [pagination.page, filters]);

  const fetchAdminUsers = async () => {
    setLoading(true);
    try {
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        ...filters,
      };

      Object.keys(params).forEach(key => {
        if (params[key] === '') delete params[key];
      });

      const response = await adminUsersAPI.getAdminUsers(params);
      setAdminUsers(response.data.data || []);
      setPagination(prev => ({ ...prev, total: response.data.total || 0 }));
    } catch (error) {
      console.error('Error fetching admin users:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    
    if (!canCreate) {
      setMessage({ type: 'error', text: 'You do not have permission to create admin users' });
      return;
    }
    
    if (formData.username.length < 4) {
      setMessage({ type: 'error', text: 'Username must be at least 4 characters' });
      return;
    }

    if (formData.password.length < 8) {
      setMessage({ type: 'error', text: 'Password must be at least 8 characters' });
      return;
    }

    try {
      await adminUsersAPI.createAdminUser(formData);
      
      setMessage({ type: 'success', text: 'Admin user created successfully!' });
      setFormData({
        username: '',
        email: '',
        password: '',
        full_name: '',
        department: '',
        phone: '',
        role: 'viewer',
      });
      setShowForm(false);
      fetchAdminUsers();
    } catch (error) {
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.detail || 'Failed to create admin user' 
      });
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    
    if (!canEdit) {
      setMessage({ type: 'error', text: 'You do not have permission to edit admin users' });
      return;
    }
    
    if (!editMode) return;

    try {
      await adminUsersAPI.updateAdminUser(editMode.id, {
        full_name: formData.full_name,
        department: formData.department,
        phone: formData.phone,
        role: formData.role,
        is_active: formData.is_active,
      });
      
      setMessage({ type: 'success', text: 'Admin user updated successfully!' });
      setEditMode(null);
      setShowForm(false);
      fetchAdminUsers();
    } catch (error) {
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.detail || 'Failed to update admin user' 
      });
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (!canEdit) {
      setMessage({ type: 'error', text: 'You do not have permission to change passwords' });
      return;
    }
    
    if (passwordData.new_password.length < 8) {
      setMessage({ type: 'error', text: 'Password must be at least 8 characters' });
      return;
    }

    try {
      await adminUsersAPI.changePassword(showPasswordModal.id, passwordData.new_password);
      
      setMessage({ type: 'success', text: 'Password changed successfully!' });
      setPasswordData({ new_password: '' });
      setShowPasswordModal(null);
    } catch (error) {
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.detail || 'Failed to change password' 
      });
    }
  };

  const handleDelete = async (admin) => {
    if (!canDelete) {
      setMessage({ type: 'error', text: 'You do not have permission to delete admin users' });
      return;
    }
    
    if (!window.confirm(`Are you sure you want to delete admin user "${admin.username}"?`)) {
      return;
    }

    try {
      await adminUsersAPI.deleteAdminUser(admin.id);
      setMessage({ type: 'success', text: 'Admin user deleted successfully!' });
      fetchAdminUsers();
    } catch (error) {
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.detail || 'Failed to delete admin user' 
      });
    }
  };

  const viewDetail = async (id) => {
    try {
      const response = await adminUsersAPI.getAdminUserDetail(id);
      setSelectedAdmin(response.data);
    } catch (error) {
      console.error('Error fetching detail:', error);
    }
  };

  const startEdit = (admin) => {
    if (!canEdit) {
      setMessage({ type: 'error', text: 'You do not have permission to edit admin users' });
      return;
    }
    setEditMode(admin);
    setFormData({
      full_name: admin.full_name || '',
      department: admin.department || '',
      phone: admin.phone || '',
      role: admin.role,
      is_active: admin.is_active,
    });
    setShowForm(true);
  };

  const roleColors = {
    super_admin: 'badge-danger',
    admin: 'badge-danger',
    manager: 'badge-warning',
    operator: 'badge-info',
    viewer: 'badge-secondary',
  };

  return (
    <div>
      <div className="page-header">
        <h1>Admin User Management</h1>
        <p>관리자 계정 관리 (개인정보보호법 제5조 - 접근 권한 관리)</p>
        
        {/* Only show Create button if user has permission */}
        {canCreate && (
          <button 
            className="btn btn-primary" 
            onClick={() => {
              setShowForm(!showForm);
              setEditMode(null);
              setFormData({
                username: '',
                email: '',
                password: '',
                full_name: '',
                department: '',
                phone: '',
                role: 'viewer',
              });
            }}
            style={{ marginTop: '10px' }}
          >
            {showForm ? 'Cancel' : '+ Create Admin User'}
          </button>
        )}
      </div>

      {message.text && (
        <div className={message.type === 'success' ? 'success' : 'error'} style={{ marginBottom: '20px' }}>
          {message.text}
        </div>
      )}

      {showForm && (canCreate || canEdit) && (
        <div className="card">
          <div className="card-header">
            <h2>{editMode ? 'Edit Admin User' : 'Create New Admin User'}</h2>
          </div>
          
          <form onSubmit={editMode ? handleUpdate : handleCreate}>
            {!editMode && (
              <>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                  <div className="form-group">
                    <label>Username * (min 4 characters)</label>
                    <input
                      type="text"
                      name="username"
                      className="form-control"
                      placeholder="admin123"
                      value={formData.username}
                      onChange={handleInputChange}
                      minLength={4}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>Email *</label>
                    <input
                      type="email"
                      name="email"
                      className="form-control"
                      placeholder="admin@puraxel.com"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>Password * (min 8 characters)</label>
                  <input
                    type="password"
                    name="password"
                    className="form-control"
                    placeholder="••••••••"
                    value={formData.password}
                    onChange={handleInputChange}
                    minLength={8}
                    required
                  />
                </div>
              </>
            )}

            {editMode && (
              <div className="form-group">
                <label>Username</label>
                <input
                  type="text"
                  className="form-control"
                  value={editMode.username}
                  disabled
                  style={{ background: '#ecf0f1' }}
                />
              </div>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
              <div className="form-group">
                <label>Full Name</label>
                <input
                  type="text"
                  name="full_name"
                  className="form-control"
                  placeholder="홍길동"
                  value={formData.full_name}
                  onChange={handleInputChange}
                />
              </div>

              <div className="form-group">
                <label>Department</label>
                <input
                  type="text"
                  name="department"
                  className="form-control"
                  placeholder="CS Team"
                  value={formData.department}
                  onChange={handleInputChange}
                />
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
              <div className="form-group">
                <label>Phone</label>
                <input
                  type="tel"
                  name="phone"
                  className="form-control"
                  placeholder="010-1234-5678"
                  value={formData.phone}
                  onChange={handleInputChange}
                />
              </div>

              <div className="form-group">
                <label>Role *</label>
                <select
                  name="role"
                  className="form-control"
                  value={formData.role}
                  onChange={handleInputChange}
                  required
                >
                  <option value="viewer">Viewer (Read Only)</option>
                  <option value="operator">Operator (View + Send Notifications)</option>
                  <option value="manager">Manager (Read + Write)</option>
                  <option value="admin">Admin (Full Access)</option>
                  <option value="super_admin">Super Admin (All Permissions)</option>
                </select>
              </div>
            </div>

            {editMode && (
              <div className="form-group">
                <label style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.checked }))}
                  />
                  Active
                </label>
              </div>
            )}

            <button type="submit" className="btn btn-success">
              {editMode ? 'Update Admin User' : 'Create Admin User'}
            </button>
          </form>
        </div>
      )}

      <div className="card">
        <div className="card-header">
          <h2>All Admin Users</h2>
        </div>

        <div className="filters">
          <div className="filter-item">
            <input
              type="text"
              className="form-control"
              placeholder="Search by username, email, or name"
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
            />
          </div>
          <div className="filter-item">
            <select
              className="form-control"
              value={filters.role}
              onChange={(e) => handleFilterChange('role', e.target.value)}
            >
              <option value="">All Roles</option>
              <option value="super_admin">Super Admin</option>
              <option value="admin">Admin</option>
              <option value="manager">Manager</option>
              <option value="operator">Operator</option>
              <option value="viewer">Viewer</option>
            </select>
          </div>
          <div className="filter-item">
            <select
              className="form-control"
              value={filters.is_active}
              onChange={(e) => handleFilterChange('is_active', e.target.value)}
            >
              <option value="">All Status</option>
              <option value="true">Active</option>
              <option value="false">Inactive</option>
            </select>
          </div>
        </div>
        
        {loading ? (
          <div className="loading">Loading admin users...</div>
        ) : adminUsers.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#7f8c8d' }}>
            No admin users found
          </div>
        ) : (
          <>
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Username</th>
                    <th>Email</th>
                    <th>Full Name</th>
                    <th>Department</th>
                    <th>Role</th>
                    <th>Status</th>
                    <th>Last Login</th>
                    <th>Login Count</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {adminUsers.map((admin) => (
                    <tr key={admin.id}>
                      <td>{admin.id}</td>
                      <td><strong>{admin.username}</strong></td>
                      <td>{admin.email}</td>
                      <td>{admin.full_name || '-'}</td>
                      <td>{admin.department || '-'}</td>
                      <td>
                        <span className={`badge ${roleColors[admin.role] || 'badge-secondary'}`}>
                          {admin.role}
                        </span>
                      </td>
                      <td>
                        <span className={`badge ${admin.is_active ? 'badge-success' : 'badge-danger'}`}>
                          {admin.is_active ? 'Active' : 'Inactive'}
                        </span>
                        {admin.is_locked && (
                          <span className="badge badge-danger" style={{ marginLeft: '5px' }}>Locked</span>
                        )}
                      </td>
                      <td>{admin.last_login_at ? new Date(admin.last_login_at).toLocaleDateString() : 'Never'}</td>
                      <td>{admin.login_count}</td>
                      <td>
                        <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap' }}>
                          {/* View - always visible */}
                          <button
                            className="btn btn-primary"
                            style={{ padding: '5px 10px', fontSize: '12px' }}
                            onClick={() => viewDetail(admin.id)}
                          >
                            View
                          </button>
                          
                          {/* Edit - only if has permission */}
                          {canEdit && (
                            <button
                              className="btn btn-secondary"
                              style={{ padding: '5px 10px', fontSize: '12px' }}
                              onClick={() => startEdit(admin)}
                            >
                              Edit
                            </button>
                          )}
                          
                          {/* Password - only if has permission */}
                          {canEdit && (
                            <button
                              className="btn btn-warning"
                              style={{ padding: '5px 10px', fontSize: '12px' }}
                              onClick={() => setShowPasswordModal(admin)}
                            >
                              Password
                            </button>
                          )}
                          
                          {/* Delete - only if has permission */}
                          {canDelete && (
                            <button
                              className="btn btn-danger"
                              style={{ padding: '5px 10px', fontSize: '12px' }}
                              onClick={() => handleDelete(admin)}
                            >
                              Delete
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="pagination">
              <button
                onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                disabled={pagination.page === 1}
              >
                Previous
              </button>
              <span>
                Page {pagination.page} of {Math.ceil(pagination.total / pagination.limit) || 1}
              </span>
              <button
                onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                disabled={pagination.page >= Math.ceil(pagination.total / pagination.limit)}
              >
                Next
              </button>
            </div>
          </>
        )}
      </div>

      {/* Password Change Modal - only if has permission */}
      {showPasswordModal && canEdit && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }}>
          <div style={{
            background: 'white',
            borderRadius: '8px',
            padding: '30px',
            maxWidth: '500px',
            width: '100%'
          }}>
            <h2>Change Password</h2>
            <p style={{ marginBottom: '20px', color: '#7f8c8d' }}>
              User: <strong>{showPasswordModal.username}</strong>
            </p>

            <form onSubmit={handleChangePassword}>
              <div className="form-group">
                <label>New Password * (min 8 characters)</label>
                <input
                  type="password"
                  className="form-control"
                  placeholder="••••••••"
                  value={passwordData.new_password}
                  onChange={(e) => setPasswordData({ new_password: e.target.value })}
                  minLength={8}
                  required
                />
              </div>

              <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
                <button type="submit" className="btn btn-success">
                  Change Password
                </button>
                <button 
                  type="button"
                  className="btn btn-secondary" 
                  onClick={() => {
                    setShowPasswordModal(null);
                    setPasswordData({ new_password: '' });
                  }}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Detail Modal - always visible */}
      {selectedAdmin && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }}>
          <div style={{
            background: 'white',
            borderRadius: '8px',
            padding: '30px',
            maxWidth: '800px',
            maxHeight: '80vh',
            overflow: 'auto',
            width: '100%'
          }}>
            <h2>Admin User Detail</h2>
            <p style={{ color: '#7f8c8d', marginBottom: '20px' }}>
              {selectedAdmin.username} ({selectedAdmin.email})
            </p>
            
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '30px' }}>
              <div>
                <p><strong>Full Name:</strong> {selectedAdmin.full_name || '-'}</p>
                <p><strong>Department:</strong> {selectedAdmin.department || '-'}</p>
                <p><strong>Phone:</strong> {selectedAdmin.phone || '-'}</p>
                <p><strong>Role:</strong> <span className={`badge ${roleColors[selectedAdmin.role] || 'badge-secondary'}`}>{selectedAdmin.role}</span></p>
              </div>
              <div>
                <p><strong>Status:</strong> {selectedAdmin.is_active ? 'Active' : 'Inactive'}</p>
                <p><strong>Locked:</strong> {selectedAdmin.is_locked ? 'Yes' : 'No'}</p>
                <p><strong>Login Count:</strong> {selectedAdmin.login_count}</p>
                <p><strong>Last Login:</strong> {selectedAdmin.last_login_at ? new Date(selectedAdmin.last_login_at).toLocaleString() : 'Never'}</p>
              </div>
            </div>

            <h3>Recent Activity ({selectedAdmin.recent_activities?.length || 0})</h3>
            {!selectedAdmin.recent_activities || selectedAdmin.recent_activities.length === 0 ? (
              <p style={{ color: '#7f8c8d', padding: '20px', textAlign: 'center' }}>No activity yet</p>
            ) : (
              <div className="table-container">
                <table>
                  <thead>
                    <tr>
                      <th>Activity</th>
                      <th>Description</th>
                      <th>IP Address</th>
                      <th>Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedAdmin.recent_activities.map((activity, idx) => (
                      <tr key={idx}>
                        <td>{activity.activity_type}</td>
                        <td style={{ maxWidth: '300px', fontSize: '12px' }}>{activity.description || '-'}</td>
                        <td style={{ fontSize: '11px' }}>{activity.ip_address || '-'}</td>
                        <td>{new Date(activity.created_at).toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <button 
              className="btn btn-secondary" 
              onClick={() => setSelectedAdmin(null)}
              style={{ marginTop: '20px' }}
            >
              Close
            </button>
          </div>
        </div>
      )}

      <div className="card" style={{ background: '#fff3cd', border: '1px solid #ffc107' }}>
        <h3>🔒 Compliance Note (개인정보보호법 제5조)</h3>
        <p style={{ marginTop: '10px', fontSize: '14px', lineHeight: '1.6' }}>
          This system manages admin access controls in compliance with Korean Personal Information Protection Act Article 5 (접근 권한의 관리).
        </p>
        <ul style={{ marginTop: '10px', fontSize: '14px', lineHeight: '1.8' }}>
          <li>Admin accounts are created with role-based permissions</li>
          <li>All permission changes are logged for audit purposes</li>
          <li>Account activity is tracked and retained for 1 year</li>
        </ul>
      </div>
    </div>
  );
}

export default AdminUsers;